






















import { Component, Mixins, Prop } from "vue-property-decorator";
import { IConfiguration } from "../../../../domain/configuration";
import { IObjectStyleReadable } from "../IObjectStyleReadable";
import { ImageObjectStyleReader } from "../styleReaders";
import {
  ClaioContentDrawableObjectMixin,
  StyledDrawableMixin,
} from "./mixins";
import { ImageItem } from "../../../../domain/document/items";
import { DomainRegistry } from "../../../../domain";
import { ImageSourceKinds, ImageKinds, IDocumentImageSource } from "../../../../domain/document";
import { FixedPhraseTextDropManager, IImageDragParameter } from "../../../dragging";
import { FileToBase64TextConverter } from "../FileToBase64TextConverter";
import movieIcon from "@/assets/icon_movie.png";

@Component
export default class ImageObject extends Mixins(
  ClaioContentDrawableObjectMixin,
  StyledDrawableMixin,
) {
  get editStyleReader(): IObjectStyleReadable {
    const configuration = this.$store.getters.configuration as IConfiguration;
    return new ImageObjectStyleReader(configuration.style.document.editor.objects.image);
  }

  @Prop()
  createImageSource: (imageText: string) => Promise<IDocumentImageSource>;

  editingSource: ImageItem;

  data(): any {
    return {
      imgWidth: null,
      imgHeight: null,
      ratioScale: 0,
      isPastingImageFromClipboard: false,
    };
  }
  imgWidth: string;
  imgHeight: string;
  ratioScale: number;
  isPastingImageFromClipboard: boolean;

  updated() {
    if (!this.editingSource.imageKind) {
      this.imgWidth = null;
      this.imgHeight = null;
      this.ratioScale = null;
    }
  }

  get freeInputStyleProperties(): any {
    return {
      pointerEvents: this.editingSource.isEditable ? null : "none",
    };
  }

  get hasImage(): boolean {
    return ((this.imageSource !== null) && (this.imageSource !== ""));
  }

  get imageName(): string {
    return this.$configuration.behavior.collaboration.claio.imageName;
  }

  get schemaName(): string {
    return this.$configuration.behavior.collaboration.schema.imageName;
  }

  get externalImageName(): string {
    return this.$configuration.behavior.collaboration.externalImage.imageName;
  }

  get ocojoImageName(): string {
    return this.$configuration.behavior.collaboration.ocojo.imageName;
  }

  get isTrimmableImage(): boolean {
    // シェーマを画像切り抜きしてしまうと原本画像との整合がおかしくなるので切り抜きの対象外としている
    // それ以外の画像では切り抜きで原本画像も更新することで辻褄を合わせている
    return this.hasImage && this.editingSource.isEditable && (this.editingSource.imageKind !== ImageKinds.ClaioSchema) && (this.editingSource.imageKind !== ImageKinds.ClaioSchemaEdit);
  }
  
  adjustImageSizeWithRetry(): void {
    this.adjustImageSize();
    this.$nextTick(() => {
      // containerのサイズを正常に取得できていないことがあるので再度設定する
      this.adjustImageSize();
    });
  }

  adjustImageSize(): void {
    const image = this.$refs.image as HTMLImageElement;
    if (!image) { return; }

    const container = this.$refs.container as HTMLElement;
    const rect = container.getBoundingClientRect();
    const ratioWidth = image.naturalWidth / (rect.width > 0 ? rect.width : 1);
    const ratioHeight = image.naturalHeight / (rect.height > 0 ? rect.height : 1);
    const ratioRotateWidth = image.naturalWidth / (rect.height > 0 ? rect.height : 1);
    const ratioRotateHeight = image.naturalHeight / (rect.width > 0 ? rect.width : 1);

    // 比率が大きい方に合わせ、containerにフィットさせる
    this.imgWidth = (ratioWidth >= ratioHeight) ? "100%" : null;
    this.imgHeight = (ratioWidth <= ratioHeight) ? "100%" : null;

    // 回転時の拡大率を算出する
    this.ratioScale = (ratioRotateWidth < ratioRotateHeight)
      ? (ratioWidth > ratioHeight)
        ? image.naturalWidth / image.naturalHeight
        : rect.width / rect.height
      : (ratioWidth < ratioHeight)
        ? image.naturalHeight / image.naturalWidth
        : rect.height / rect.width;
  }

  onDragOver(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  onDrop(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();

    // 定型文をドロップした場合、パラメータがクリアされないためここでクリアする
    const manager = new FixedPhraseTextDropManager();
    manager.parameter = null;

    if (!this.editingSource.isEditable) { return; }

    // Drag側で第一引数(format)が"text"でないとIEでエラーになる。
    // 現状ではブラウザ外からテキストをD&Dしたときと判別できないため、
    // IEでもエラーにならないページ内D&D用のformatを見つけるか、store経由で値を渡すなどの対応したほうがよさそう
    try {
      const text = event.dataTransfer.getData("text");
      if (!text) {
        const file = event.dataTransfer.files[0];
        if (file.type.indexOf("image/") !== 0) {
          return;
        }
        this.uploadImageFile(file);
        return;
      }
      this.$logger.debug(text);
      const data = this.parseAsClaioImageDragParameter(text);
      if (!data) { return; }

      const patient = this.commonComponents.adapters.claio.patient;
      if (data.isClaioImage) {
        const param = data.claioImageDragParameter;
        if (param.patientId !== patient.id) { throw new Error("画像の患者IDが一致していません。"); }
        this.registerClaioImage(param.patientId, param.claioImageId, param.imageDepartmentId, param.isVisibleLayer, param.isMovie, param.isSecret);
      }
      if (data.isClaioSchema) {
        const param = data.claioSchemaDragParameter;
        this.registerClaioSchema(param.schemaTemplateId, param.schemaGroupId, param.schemaItemId);
      }
      if (data.isExternalImage) {
        const param = data.externalImageDragParameter;
        if (param.patientId !== patient.id) { throw new Error("画像の患者IDが一致していません。"); }
        this.registerExternalImage(param.patientId, param.externalImageId);
      }
      if (data.isOcojoImage) {
        const param = data.ocojoImageDragParameter;
        if (param.patientId !== patient.id) { throw new Error("画像の患者IDが一致していません。"); }
        this.registerOcojoImage(param.patientId, param.imageId, param.imageDepartmentId, param.isVisibleLayer, param.isMovie, param.isSecret);
      }
      if (data.isRegisteredImage) {
        // TODO: 複数の編集画面を表示してD&Dされたときの対策
        const param = data.registeredImageDragParameter;
        if (param.patientId !== patient.id) { throw new Error("画像の患者IDが一致していません。"); }
        const itemSetId = this.commonComponents.editors.imageSwapper.getCurrentSectionItemSetId();
        this.commonComponents.editors.imageSwapper.swap(itemSetId, param.privateId, this.editingSource.privateId);
      }
    } catch (e) {
      this.$error("画像登録失敗", "画像の登録に失敗しました。", e);
    }
  }

  private parseAsClaioImageDragParameter(text: string): IImageDragParameter {
    try {
      return JSON.parse(text) as IImageDragParameter;
    } catch (e) {
      this.$logger.debug("Failed to parse JSON");
      return null;
    }
  }

  uploadImageFile(file: File): void {
    FileToBase64TextConverter.convert(file, this.upload, () => {
      this.$error(
        "読み込み失敗",
        "ファイルの読み込みに失敗しました。",
      );
    });
  }

  private uploadDocumentImage(imageText: string) {
    const documentRepository = DomainRegistry.document.getDocumentRepository();
    documentRepository.uploadDocumentImage(imageText)
    .then(imageGuid => {
      this.$logger.debug("upload done. imageGuid: " + imageGuid);

      // numberではなくてGUIDが返ってくる模様
      this.writeProperty((s: ImageItem) => {
        s.clearImageInformation();
        // TODO: refactor
        s.imageSource.imageGuid = imageGuid;
        s.imageSource.imageSourceKind = ImageSourceKinds.Cache;
        s.originalImageSource.imageGuid = imageGuid;
        s.originalImageSource.imageSourceKind = ImageSourceKinds.Cache;
        s.imageKind = ImageKinds.Image;
      });
    })
    .catch(error => {
      this.$error(
        "アップロード失敗",
        "ファイルのアップロードに失敗しました。",
        error,
      );
    });
  }

  private createDocumentImageSource(imageText: string) {
    this.createImageSource(imageText)
    .then(s => {
      this.writeProperty((i: ImageItem) => {
        i.clearImageInformation();
        i.replaceImageSource(s);
        i.imageKind = ImageKinds.Image;
      });
    }).catch(e => {
      this.$error(
        "アップロード失敗",
        "ファイルのアップロードに失敗しました。",
        e,
      );
    });
  }

  upload(imageText: string) {
    if (this.createImageSource) {
      this.createDocumentImageSource(imageText);
    } else {
      this.uploadDocumentImage(imageText);
    }
  }

  registerClaioImage(patientId: string, claioImageId: string, imageDepartmentId: string, isVisibleLayer: boolean, isMovie: boolean, isSecret: boolean): void {
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createImageSourceFromClaioImage(patientId, claioImageId, isVisibleLayer, isMovie, isSecret, imageDepartmentId)
    .then(imageSource => {
      if (!imageSource) { return; }
      if (isSecret) {
        // TODO: isSecret対応
        this.$notImplemented("シークレット画像への対応は未実装です。");
      }
      this.writeProperty((s: ImageItem) => {
        s.clearImageInformation();
        s.replaceImageSource(imageSource);
        s.imageKind = isMovie ? ImageKinds.ClaioMovie : ImageKinds.ClaioImage;
        s.claioImageId = claioImageId;
        s.imageDepartmentId = imageDepartmentId;
      });
    })
    .catch(error => {
      console.error("image upload failed!!!");
      this.$error(
        this.imageName + "登録失敗",
        this.imageName + "の登録に失敗しました。",
        error,
      );
    });
  }

  registerClaioSchema(templateId: number, groupId: number, itemId: number): void {
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createImageSourceFromClaioSchema(templateId, groupId, itemId)
    .then(imageSource => {
      if (!imageSource) { return; }
      this.writeProperty((s: ImageItem) => {
        s.clearImageInformation();
        s.replaceImageSource(imageSource);
        s.imageKind = ImageKinds.ClaioSchema;
      });
    })
    .catch(error => {
      console.error("schema image upload failed!!!");
      this.$error(
        this.schemaName + "登録失敗",
        this.schemaName + "の登録に失敗しました。",
        error,
      );
    });
  }

  registerExternalImage(patientId: string, externalImageId: string): void {
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createImageSourceFromExternalImage(patientId, externalImageId)
    .then(imageSource => {
      if (!imageSource) { return; }
      this.writeProperty((s: ImageItem) => {
        s.clearImageInformation();
        s.replaceImageSource(imageSource);
        s.imageKind = ImageKinds.Image;
        s.externalImageId = externalImageId;
      });
    })
    .catch(error => {
      console.error("external image upload failed!!!");
      this.$error(
        this.externalImageName + "登録失敗",
        this.externalImageName + "の登録に失敗しました。",
        error,
      );
    });
  }

  registerOcojoImage(patientId: string, imageId: string, imageDepartmentId: string, isVisibleLayer: boolean, isMovie: boolean, isSecret: boolean): void {
    // OcojoとClaioで文書に貼り付けた時のパラメータも分けるべきだが、既に運用始まってるので簡単には変えられないので、ここはClaioと同様にしておく.
    // OcojoとClaioを同時に利用したいニーズが出てきたら回避策を検討する事.
    // TODO: Ocojo用のドラッグ実装を追加する.
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createImageSourceFromClaioImage(patientId, imageId, isVisibleLayer, isMovie, isSecret, imageDepartmentId)
    .then(imageSource => {
      if (!imageSource) { return; }
      if (isSecret) {
        // TODO: isSecret対応
        this.$notImplemented("シークレット画像への対応は未実装です。");
      }
      this.writeProperty((s: ImageItem) => {
        s.clearImageInformation();
        s.replaceImageSource(imageSource);
        s.imageKind = isMovie ? ImageKinds.ClaioMovie : ImageKinds.ClaioImage;
        s.claioImageId = imageId;
        s.imageDepartmentId = imageDepartmentId;
      });
    })
    .catch(error => {
      console.error("image upload failed!!!");
      this.$error(
        this.ocojoImageName + "登録失敗",
        this.ocojoImageName + "の登録に失敗しました。",
        error,
      );
    });
  }

  onDoubleclicked(): void {
    this.launchClaioApplicationWhenNeeded()
    .then(isHandled => {
      if (!isHandled) {
        this.showUploadDialog();
      }
    });
  }
  
  showUploadDialog(): void {
    if (!this.editingSource.isEditable) { return; }
    this.emitFocus();
    this.commonComponents.dialogs.imageFileUploader.show(this.hasImage)
    .then(source => {
      if (source === null) {
        return;
      } else if (source.imageSourceKind === ImageSourceKinds.None) {
        this.writeProperty((s: ImageItem) => {
          s.clearImageInformation();
          s.replaceImageSource(source);
        });
        return;
      }
      this.$logger.debug("imageGuid: " + source.imageGuid);
      this.writeProperty((s: ImageItem) => {
        s.clearImageInformation();
        s.replaceImageSource(source);
        s.imageKind = ImageKinds.Image;
      });
    });
  }

  get rotationStyleProperties(): any {
    if (!this.editingSource.rotationAngle) { return null; }
    if (!this.ratioScale) { return null; }
    const transformText = "rotate(" + this.editingSource.rotationAngle + "deg) " + this.getScaleText(this.editingSource.rotationAngle);
    return {
      transform: transformText,
    };
  }

  getScaleText(angle: number): string {
    return ((angle % 180 !== 0) && this.ratioScale)
      ? "scale(" + this.ratioScale + ")"
      : "";
  }

  get isMovieImage(): boolean {
    return (this.editingSource.imageKind === ImageKinds.ClaioMovie);
  }

  get movieIconImageSource() {
    return movieIcon;
  }

  get maxSizeStyleProperties(): any {
    return {
      maxWidth: this.size.width + "px",
      maxHeight: this.size.height + "px",
    };
  }

  async trimImage(): Promise<void> {
    if (!this.isTrimmableImage) {
      this.$logger.warn("画像切り抜き不可としている画像です。実装を確認してください。");
      return;
    }
    try {
      const result = await this.commonComponents.dialogs.trimming.show(this.imageSource);
      if (!result || !result.imageDataText) { return; }
      const imageSource = await this.createImageSourceFromImageDataText(result.imageDataText);
      this.writeProperty((s: ImageItem) => {
        // ClaioSchemaの挙動と辻褄を合わせるのが困難のため、原本画像も更新している
        // ClaioImageIdなどは維持したいのでclearImageInformation()は呼ばない
        s.replaceImageSource(imageSource);
      });
    } catch (e) {
      this.$error("画像切り抜き", "処理中にエラーが発生しました。", e);
    }
  }

  private async createImageSourceFromImageDataText(imageDataText: string): Promise<IDocumentImageSource> {
    if (this.createImageSource) {
      return this.createImageSource(imageDataText);
    }
    const documentRepository = DomainRegistry.document.getDocumentRepository();
    return documentRepository.createImageSource(imageDataText);
  }

  editImage(): void {
    this.editClaioWhenNeeded();
  }

  editImages(): void {
    this.editClaioImagesWhenNeeded();
  }

  clearImage(): void {
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createEmptyImageSource()
    .then(source => {
      this.writeProperty((s: ImageItem) => {
        s.clearImageInformation();
        s.replaceImageSource(source);
      });
    });
  }

  get isPastableFromClipboard(): boolean {
    return DomainRegistry.localhost.getImageClipboardService().isAvailable();
  }

  async pasteImageFromClipboard(): Promise<void> {
    try {
      this.isPastingImageFromClipboard = true;
      const guid = await DomainRegistry.localhost.getImageClipboardService().registerClippedImage();
      if (!guid) {
        this.$warn("貼り付け失敗", "クリップボードに画像がありませんでした.");
      } else {
        this.writeProperty((s: ImageItem) => {
          s.clearImageInformation();
          s.imageSource.imageGuid = guid;
          s.imageSource.imageSourceKind = ImageSourceKinds.Cache;
          s.originalImageSource.imageGuid = guid;
          s.originalImageSource.imageSourceKind = ImageSourceKinds.Cache;
          s.imageKind = ImageKinds.Image;
        });
      }
    } catch (e) {
      this.$error("貼り付け失敗", "クリップボード画像登録中にエラーが発生しました.", e);
    } finally {
      this.isPastingImageFromClipboard = false;
    }
  }

  onDragStart(event: DragEvent): void {
    const patient = this.commonComponents.adapters.claio.patient;
    const data: IImageDragParameter = {
      isRegisteredImage: true,
      registeredImageDragParameter: {
        privateId: this.editingSource.privateId,
        patientId: patient.id,
      }
    };
    const text = JSON.stringify(data);
    // 第一引数(format)が"text"でないとIEでエラーになる。
    event.dataTransfer.setData("text", text);
  }
}
